import { FC, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { Form } from 'antd';

import { StepTwoFormContext } from './StepTwoFormContext';

import { StepTwoFireForm } from '../../../../interfaces/steps-forms.interface';
import { QuotationInfoContext, QuotationStep } from '../../../../../shared';
import { WizardDataFormContext } from '../../../../Wizard/contexts/WizardDataForm';
import { ActividadesComercio } from '../../../../../../../../../shared/interfaces';
import { LOADING_SAVED_QUOTATIONS_STATES } from '../../../../../shared/interfaces/loading-saved-quotations-states-enum';
import { LOADING_RENEWAL_QUOTATION_STATES } from '../../../../../shared/interfaces/loading-renewal-quotation-states-enum';
import { LoadQuotationToRenewResponse } from '../../../../../shared/interfaces/load-renewal-quotation-response-interface';

export const StepTwoFormProvider: FC<PropsWithChildren> = ({ children }) => {
  const { 
    savedQuotation, 
    loadingSavedQuotation, 
    currentStep, 
    loadSaveQuotationState, 
    setLoadSaveQuotationState ,
    renewalQuotationState,
    setRenewalQuotationState,
    quotationToRenewal,
    loadingRenewalQuotation
  } = useContext(QuotationInfoContext);
  const { rubricList, getRubricList } = useContext(WizardDataFormContext);
  const [stepTwoForm] = Form.useForm<StepTwoFireForm>();
  const [rubricSelected, setRubricSelected] = useState<ActividadesComercio>();

  const fillStepTwoServices = useCallback(async () => {
    try {
      await getRubricList();
    } catch (error) {}
  }, [getRubricList]);

  const fillStepTwoData = useCallback(async (isRenewal: boolean) => {
    let quotationToProccess: LoadQuotationToRenewResponse;
    if(isRenewal){
      quotationToProccess = quotationToRenewal;
    }else{
      quotationToProccess = savedQuotation;
    }
    const valorInciso1 = quotationToProccess?.capituloDeTarifaInciso1 ?? '--';
    const valorInciso2 = quotationToProccess?.capituloDeTarifaInciso2 ?? '--';
    const rubric = `${quotationToProccess.capituloDeTarifa}${valorInciso1}${valorInciso2}`;

    stepTwoForm.setFieldValue('category', rubric)
    
    const rubricSelected = rubricList?.actividadesComercio.find(
      (item: ActividadesComercio) =>
        `${item.capituloTarifa}${item.capituloTarifaInciso1}${item.capituloTarifaInciso2}` === rubric
    );
    setRubricSelected(rubricSelected);
  }, [stepTwoForm, savedQuotation, quotationToRenewal, rubricList, setRubricSelected]);

  useEffect(() => {
    if (currentStep === QuotationStep.TWO && !rubricList && !loadingSavedQuotation) {
      getRubricList();
    }
  }, [currentStep, rubricList, loadingSavedQuotation]);

  useEffect(() => {
    if(loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.STEP_ONE_DATA_FILLED) {
      setLoadSaveQuotationState(LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_SERVICES_LOADING);
      fillStepTwoServices();
    }
  }, [loadSaveQuotationState]);

  useEffect(() => {
    if(loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_SERVICES_LOADING &&
      rubricList
    ) {
      setLoadSaveQuotationState(LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_SERVICES_LOADED);
    }
  }, [loadSaveQuotationState, rubricList]);
  
  useEffect(() => {
    if(loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_SERVICES_LOADED) {
      setLoadSaveQuotationState(LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_DATA_FILLING);
      fillStepTwoData(false);
    }
  }, [loadSaveQuotationState]);

  useEffect(() => {
    if(loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_DATA_FILLING &&
      rubricSelected) {
      setLoadSaveQuotationState(LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_DATA_FILLED);
    }
  }, [loadSaveQuotationState, rubricSelected]);
  
  /**
   * Renewal
   */

  useEffect(() => {
    if (currentStep === QuotationStep.TWO && !rubricList && !loadingRenewalQuotation) {
      getRubricList();
    }
  }, [currentStep, rubricList, loadingRenewalQuotation]);

  useEffect(() => {
    if(renewalQuotationState === LOADING_RENEWAL_QUOTATION_STATES.STEP_ONE_DATA_FILLED_RENEWAL) {
      setRenewalQuotationState(LOADING_RENEWAL_QUOTATION_STATES.STEP_TWO_SERVICES_LOADING_RENEWAL);
      fillStepTwoServices();
    }
  }, [renewalQuotationState]);

  useEffect(() => {
    if(renewalQuotationState === LOADING_RENEWAL_QUOTATION_STATES.STEP_TWO_SERVICES_LOADING_RENEWAL &&
      rubricList
    ) {
      setRenewalQuotationState(LOADING_RENEWAL_QUOTATION_STATES.STEP_TWO_SERVICES_LOADED_RENEWAL);
    }
  }, [renewalQuotationState, rubricList]);
  
  useEffect(() => {
    if(renewalQuotationState === LOADING_RENEWAL_QUOTATION_STATES.STEP_TWO_SERVICES_LOADED_RENEWAL) {
      setRenewalQuotationState(LOADING_RENEWAL_QUOTATION_STATES.STEP_TWO_DATA_FILLING_RENEWAL);
      fillStepTwoData(true);
    }
  }, [renewalQuotationState]);

  useEffect(() => {
    if(renewalQuotationState === LOADING_RENEWAL_QUOTATION_STATES.STEP_TWO_DATA_FILLING_RENEWAL &&
      rubricSelected) {
      setRenewalQuotationState(LOADING_RENEWAL_QUOTATION_STATES.STEP_TWO_DATA_FILLED_RENEWAL);
    }
  }, [renewalQuotationState, rubricSelected]);

  const values = useMemo(() => ({
    stepTwoForm,
    rubricSelected,
    setRubricSelected
  }), [
    stepTwoForm,
    rubricSelected,
    setRubricSelected
  ]);

  return (
    <StepTwoFormContext.Provider value={values}>
      {children}
    </StepTwoFormContext.Provider>
  );
};
